import { Rng } from '../../shared';
import type { MapPlace, MapPoint } from './mapModel';

const COLS = 36, ROWS = 20, CELL = 20;
const isWater = (p: MapPlace): boolean => p.id === 'bridge' || p.id === 'ferry';
const point = (cell: number): MapPoint => ({ x: 10 + cell % COLS * CELL, y: 10 + Math.floor(cell / COLS) * CELL });
const cell = (p: MapPoint): number => Math.max(0, Math.min(COLS - 1, Math.round((p.x - 10) / CELL)))
  + Math.max(0, Math.min(ROWS - 1, Math.round((p.y - 10) / CELL))) * COLS;

/** Ink rivers bend through empty land; they never cross a non-water vignette. */
export function waterwaysFor(places: MapPlace[], seed: string): MapPoint[][] {
  const waters = places.filter(isWater);
  if (!waters.length) return [];
  const dry = places.filter(p => !isWater(p));
  const blocked = new Set<number>();
  for (let i = 0; i < COLS * ROWS; i++) {
    const p = point(i);
    if (dry.some(q => Math.abs(p.x - q.x) < 74 && p.y > q.y - 123 && p.y < q.y + 52)) blocked.add(i);
  }
  const directions = new Rng(seed).stream('map:water').shuffle([[1, 0], [0, 1], [-1, 0], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]]);
  function route(from: MapPoint, to: MapPoint): MapPoint[] {
    const start = cell(from), goal = cell(to), open = [start], came = new Map<number, number>();
    if (blocked.has(goal)) return [];
    const cost = new Map([[start, 0]]), closed = new Set<number>();
    const heuristic = (c: number): number => Math.hypot(c % COLS - goal % COLS, Math.floor(c / COLS) - Math.floor(goal / COLS));
    while (open.length) {
      open.sort((a, b) => cost.get(a)! + heuristic(a) - cost.get(b)! - heuristic(b));
      const at = open.shift()!;
      if (at === goal) {
        const cells = [at]; let cursor = at;
        while (came.has(cursor)) { cursor = came.get(cursor)!; cells.push(cursor); }
        const raw = [from, ...cells.reverse().map(point), to];
        return raw.filter((p, i) => i === 0 || i === raw.length - 1 || (p.x - raw[i-1].x) * (raw[i+1].y - p.y) !== (p.y - raw[i-1].y) * (raw[i+1].x - p.x));
      }
      closed.add(at);
      for (const [dx, dy] of directions) {
        const x = at % COLS + dx, y = Math.floor(at / COLS) + dy, next = y * COLS + x;
        if (x < 0 || x >= COLS || y < 0 || y >= ROWS || closed.has(next) || (next !== goal && blocked.has(next))) continue;
        if (dx && dy && (blocked.has(at + dx) || blocked.has(at + dy * COLS))) continue;
        const previous = came.get(at), bend = previous !== undefined && at - previous !== next - at ? .28 : 0;
        const nextCost = cost.get(at)! + Math.hypot(dx, dy) + bend;
        if (nextCost < (cost.get(next) ?? Infinity)) {
          cost.set(next, nextCost); came.set(next, at); if (!open.includes(next)) open.push(next);
        }
      }
    }
    return [];
  }
  const centers = waters.map(p => ({ x: p.x + (p.id === 'bridge' ? 15 : 0), y: p.y + (p.id === 'bridge' ? 25 : -16) }));
  const rivers: MapPoint[][] = [];
  if (centers.length === 2) rivers.push(route(centers[0], centers[1]));
  for (const [i, join] of centers.entries()) {
    const center = waters[i].id === 'bridge' ? { x: waters[i].x - 17, y: waters[i].y - 44 } : join;
    const edges = [{ x: 10, y: center.y }, { x: 710, y: center.y }, { x: center.x, y: 10 }, { x: center.x, y: 390 }];
    const exits = edges.map(p => route(center, p)).filter(points => points.length > 1);
    exits.sort((a, b) => length(a) - length(b));
    if (exits[0]) rivers.push(exits[0]);
  }
  return rivers.filter(points => points.length > 1);
}
function length(points: MapPoint[]): number {
  return points.slice(1).reduce((sum, p, i) => sum + Math.hypot(p.x - points[i].x, p.y - points[i].y), 0);
}
